import { useState, useRef, useEffect } from "react";
import { Warning, Check, X, ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

const CHECKLIST = [
  "Spelling and grammar","Names and titles","Telephone numbers","Email addresses",
  "Website addresses","Social media details","Dates and times","Prices and promotional information",
  "QR codes","Images and photographs","Logos and branding","Colours and layout","Terms and conditions",
];

const STATEMENTS = [
  "I have reviewed the artwork in full.",
  "I approve the artwork for production.",
  "All text, images, contact details, pricing, dates, and information have been checked and are correct.",
  "I understand that changes requested after approval may incur additional charges.",
  "I authorise Borrow A Geek to proceed with printing, publication, or production of the approved artwork.",
];

// Render the typed name in a handwriting font onto an offscreen canvas and return a PNG data URL.
function renderSignatureDataUrl(name) {
  const canvas = document.createElement("canvas");
  canvas.width = 600; canvas.height = 160;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#0B1240";
  ctx.font = "700 84px 'Caveat', 'Homemade Apple', cursive";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText(name, 20, canvas.height / 2 + 4);
  return canvas.toDataURL("image/png");
}

export default function ApprovalForm({ jobId, onSubmitted, customerName, businessName }) {
  const [checklist, setChecklist] = useState(Object.fromEntries(CHECKLIST.map((c) => [c, false])));
  const [statements, setStatements] = useState(false);
  const [status, setStatus] = useState("approved");
  const [comments, setComments] = useState("");
  const [signatoryName, setSignatoryName] = useState(customerName || "");
  const [signatoryPos, setSignatoryPos] = useState("");
  const [signatoryOrg, setSignatoryOrg] = useState(businessName || "");
  const [typedSignature, setTypedSignature] = useState("");
  const [confirmSignature, setConfirmSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fontReadyRef = useRef(false);

  useEffect(() => {
    // Wait for the Caveat font to be loaded before rendering canvas
    if (document.fonts && document.fonts.load) {
      document.fonts.load("84px Caveat").then(() => { fontReadyRef.current = true; });
    }
  }, []);

  const allChecked = Object.values(checklist).every(Boolean);

  const submit = async () => {
    if (!statements) return toast.error("You must agree to the statements");
    if (!signatoryName.trim()) return toast.error("Signatory name required");
    if (!typedSignature.trim()) return toast.error("Please type your name to sign");
    if (!confirmSignature) return toast.error("Please confirm your electronic signature");
    setSubmitting(true);
    try {
      const signaturePng = renderSignatureDataUrl(typedSignature.trim());
      await api.post(`/jobs/${jobId}/approval`, {
        checklist, statements_agreed: statements, status, comments,
        signatory_name: signatoryName, signatory_position: signatoryPos, signatory_org: signatoryOrg,
        signature_data_url: signaturePng,
      });
      toast.success("Approval submitted");
      onSubmitted?.();
    } catch { toast.error("Failed to submit approval"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="sticker p-6 md:p-8 space-y-6" data-testid="approval-form">
      <div className="border-l-4 border-[var(--gc-danger)] bg-red-50 p-4 flex gap-3">
        <Warning size={24} className="text-[var(--gc-danger)] shrink-0" weight="fill"/>
        <div>
          <div className="serif text-xl text-[var(--gc-danger)]">Please Review Carefully</div>
          <div className="text-sm text-[var(--gc-navy)] mt-1">Your approval is required before we can proceed to print. Please check all details thoroughly to avoid costly errors.</div>
        </div>
      </div>

      <div>
        <div className="chip mb-3">// checklist — review each</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {CHECKLIST.map((item) => (
            <label key={item} className="flex items-start gap-2 p-3 border border-[var(--gc-border)] hover:border-[var(--gc-orange)] cursor-pointer bg-white" data-testid={`checklist-${item.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
              <input type="checkbox" checked={!!checklist[item]} onChange={(e) => setChecklist({ ...checklist, [item]: e.target.checked })}
                className="mt-1 accent-[var(--gc-orange)] w-4 h-4" />
              <span className="text-sm font-semibold text-[var(--gc-navy)]">{item}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="border-l-4 border-[var(--gc-navy)] bg-[var(--gc-bg-alt)] p-4">
        <div className="chip chip-navy mb-2">// important notice</div>
        <ul className="text-sm text-[var(--gc-navy)] space-y-1 list-disc pl-5 mt-2">
          <li>Once written approval is received, artwork will be deemed approved for production.</li>
          <li>Any errors, omissions, or changes after approval may incur additional design, reprint, or production costs.</li>
          <li>Borrow A Geek cannot accept responsibility for content errors present in the proof and subsequently approved.</li>
        </ul>
      </div>

      <div>
        <div className="chip mb-2">// client approval statements</div>
        <label className="flex items-start gap-3 p-4 border border-[var(--gc-border)] bg-white cursor-pointer" data-testid="approval-statements-agree">
          <input type="checkbox" checked={statements} onChange={(e) => setStatements(e.target.checked)} className="mt-1 accent-[var(--gc-orange)] w-5 h-5" />
          <div className="text-sm text-[var(--gc-navy)] space-y-1">
            {STATEMENTS.map((s, i) => (<div key={i}><strong>{i + 1}.</strong> {s}</div>))}
          </div>
        </label>
      </div>

      <div>
        <div className="chip mb-2">// approval status</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { v: "approved", label: "Approved – Proceed", ico: <Check size={16} weight="bold"/> },
            { v: "approved_with_amendments", label: "Approved w/ Amendments", ico: <Warning size={16} weight="bold"/> },
            { v: "not_approved", label: "Not Approved – Changes", ico: <X size={16} weight="bold"/> },
          ].map((o) => (
            <button key={o.v} onClick={() => setStatus(o.v)}
              className={`p-4 border text-sm text-left flex items-center gap-2 font-semibold transition-all ${status === o.v ? 'border-[var(--gc-orange)] bg-[var(--gc-orange-tint)] text-[var(--gc-navy)]' : 'border-[var(--gc-border)] bg-white text-[var(--gc-navy)] hover:border-[var(--gc-navy)]'}`}
              data-testid={`approval-status-${o.v}`}>
              {o.ico} {o.label}
            </button>
          ))}
        </div>
      </div>

      {status !== "approved" && (
        <div>
          <div className="tech-label mb-2">Comments / Amendments</div>
          <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={4}
            className="gc-input" placeholder="List the amendments required…" data-testid="approval-comments" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <div className="tech-label mb-2">Name</div>
          <input value={signatoryName} onChange={(e) => setSignatoryName(e.target.value)} className="gc-input" data-testid="signatory-name" />
        </div>
        <div>
          <div className="tech-label mb-2">Position</div>
          <input value={signatoryPos} onChange={(e) => setSignatoryPos(e.target.value)} className="gc-input" data-testid="signatory-position" />
        </div>
        <div>
          <div className="tech-label mb-2">Organisation</div>
          <input value={signatoryOrg} onChange={(e) => setSignatoryOrg(e.target.value)} className="gc-input" data-testid="signatory-org" />
        </div>
      </div>

      {/* Typed signature */}
      <div>
        <div className="chip mb-3">// electronic signature</div>

        <div className="border-l-4 border-[var(--gc-orange)] bg-[var(--gc-orange-tint)] p-4 mb-4 flex gap-3" data-testid="signature-legal-notice">
          <ShieldCheck size={22} className="text-[var(--gc-orange)] shrink-0 mt-0.5" weight="fill"/>
          <div className="text-sm text-[var(--gc-navy)]">
            <div className="font-semibold mb-1">Electronic signatures are legally valid.</div>
            <p>By typing your full name below, you agree that this constitutes your legally binding electronic signature. Electronic signatures are recognised as legally equivalent to handwritten signatures under the <em>Electronic Commerce Act 2000 (Ireland)</em> and the EU <em>eIDAS Regulation (No 910/2014)</em>.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
          <div>
            <div className="tech-label mb-2">Type your full name to sign</div>
            <input
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              className="gc-input"
              placeholder="Your full legal name"
              data-testid="typed-signature-input"
              autoComplete="off"
            />
          </div>
        </div>

        {/* Live signature preview */}
        <div className="mt-4">
          <div className="tech-label mb-2">Signature preview</div>
          <div className="border border-[var(--gc-navy)] bg-white px-6 py-6 min-h-[110px] flex items-center" data-testid="signature-preview">
            {typedSignature.trim() ? (
              <div className="font-signature text-[var(--gc-navy)] text-5xl md:text-6xl leading-none">
                {typedSignature}
              </div>
            ) : (
              <div className="text-[var(--gc-text-muted)] text-sm italic">Your signature will appear here as you type…</div>
            )}
          </div>
          <div className="border-t border-[var(--gc-navy)] mt-1" />
          <div className="tech-label mt-2">Signed on {new Date().toLocaleDateString('en-IE', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
        </div>

        <label className="flex items-start gap-3 mt-4 p-3 border border-[var(--gc-border)] bg-white cursor-pointer" data-testid="signature-confirm-check">
          <input type="checkbox" checked={confirmSignature} onChange={(e) => setConfirmSignature(e.target.checked)} className="mt-1 accent-[var(--gc-orange)] w-4 h-4" />
          <span className="text-sm text-[var(--gc-navy)]">
            I confirm the signature above is mine and I intend to be legally bound by it.
          </span>
        </label>
      </div>

      <button onClick={submit} disabled={!allChecked || submitting}
        className="btn-primary w-full py-4 text-base disabled:opacity-50 justify-center"
        data-testid="submit-approval-btn">
        {submitting ? "Submitting…" : `Submit ${status === 'approved' ? 'Sign-Off' : status === 'approved_with_amendments' ? 'w/ Amendments' : 'Rejection'}`}
      </button>
      {!allChecked && <div className="tech-label text-[var(--gc-warning)] text-center">Please tick every checklist item before submitting.</div>}
    </div>
  );
}
